import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import TrainingTaskModal from "../../pages/Training/TrainingTaskModal";
import Pagination from "../../components/Pagination";
import CompanyAddress from "../../components/CompanyAddress";
import "../../styles/scormModal.css";
import "../../styles/trainingQuiz.css";
import logo from "../../images/logo.png";
import { toDateOnly } from "../../utils/dateOnly";

const ITEMS_PER_PAGE = 10;
const DEFAULT_PASSING_SCORE = 80;
const sortByModuleName = (left, right) =>
  String(left.tm_title || "").localeCompare(String(right.tm_title || ""), "en", {
    sensitivity: "base",
    numeric: true,
  });

const formatDate = (value, includeTime = false) => {
  if (!value) return "";
  if (!includeTime) return toDateOnly(value);

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStatus = (status, score, passingScore = DEFAULT_PASSING_SCORE) => {
  if (status === "expired") return "expired";
  if (status === "partially-complete" || status === "in-progress") {
    return "partially-complete";
  }
  if (
    status === "verified" ||
    (status === "complete" &&
      (score == null || Number(score) >= Number(passingScore))) ||
    (score != null && Number(score) >= Number(passingScore))
  ) {
    return "verified";
  }
  if (status === "redo" || score != null) return "redo";
  return "not-started";
};

const TRAINING_STATUS_ORDER = {
  "not-started": 1,
  "partially-complete": 2,
  redo: 3,
  expired: 4,
  verified: 5,
};

const sortAssignedTrainings = (left, right) => {
  const leftStatus = getStatus(left.status, left.score, left.passingScore);
  const rightStatus = getStatus(right.status, right.score, right.passingScore);
  const statusDifference =
    TRAINING_STATUS_ORDER[leftStatus] - TRAINING_STATUS_ORDER[rightStatus];
  if (statusDifference !== 0) return statusDifference;

  const orderDifference =
    (Number(left.tm_order) || 999999) - (Number(right.tm_order) || 999999);
  return orderDifference || sortByModuleName(left, right);
};

const STATUS_CONFIG = {
  "not-started": { icon: "●", label: "Not Started" },
  "partially-complete": { icon: "◆", label: "Partially Complete" },
  redo: { icon: "↻", label: "Re-do" },
  verified: { icon: "✓", label: "Verified" },
  expired: { icon: "⌛", label: "Expired" },
};

function StatusIndicator({ status, showLabel = false }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG["not-started"];
  return (
    <span
      className={`training-status training-status-${status}`}
      title={config.label}
      aria-label={config.label}
    >
      <span className="training-status-symbol" aria-hidden="true">
        {config.icon}
      </span>
      {showLabel && <span>{config.label}</span>}
    </span>
  );
}

const answerText = (letters, options = []) => {
  if (!Array.isArray(letters) || letters.length === 0) return "No answer";
  return letters
    .map((letter) => {
      const index = String(letter).toUpperCase().charCodeAt(0) - 65;
      const option = options[index];
      return option ? `${letter}. ${option}` : String(letter);
    })
    .join(", ");
};

function TrainingPanel() {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const loggedInId = localStorage.getItem("userId");
  const isAdmin = localStorage.getItem("userRole") === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;
  const initialOpenKey = location.state?.openModuleKey || null;
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [activeTab, setActiveTab] = useState("assigned");
  const [trainings, setTrainings] = useState([]);
  const [history, setHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [activeModule, setActiveModule] = useState("");
  const [activeTitle, setActiveTitle] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedScore, setSelectedScore] = useState(null);
  const [selectedAttempt, setSelectedAttempt] = useState(null);

  const fetchTrainingData = useCallback(async () => {
    try {
      const headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };
      const historyRequest = isAdmin
        ? axios.get(`${BASE_URL}/api/trainings/history/${userId}`, { headers })
        : Promise.resolve({ data: [] });
      const [assignedResponse, historyResponse] = await Promise.all([
        axios.get(`${BASE_URL}/api/trainings/${userId}`, { headers }),
        historyRequest,
      ]);

      const internalModules = assignedResponse.data.internalModules || [];
      setTrainings(
        internalModules
          .map((item) => ({
            ...item,
            status: item.tp_status || "not-started",
            score: item.tp_score ?? null,
            passingScore: item.passing_score ?? DEFAULT_PASSING_SCORE,
            startDate: formatDate(item.tp_start_date),
            finishDate: formatDate(item.tp_finish_date),
            expiryDate: formatDate(item.tp_expiry_date),
            deadline: formatDate(item.tm_deadline),
          }))
          .sort(sortAssignedTrainings),
      );
      setHistory(
        (historyResponse.data || [])
          .map((attempt) => ({
            ...attempt,
            attemptedDate: formatDate(attempt.tah_attempted_at),
          }))
          .sort(sortByModuleName),
      );
    } catch (error) {
      console.error("Failed to load training data", error);
    }
  }, [BASE_URL, isAdmin, userId]);

  useEffect(() => {
    fetchTrainingData();
  }, [fetchTrainingData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const handleOpenQuiz = (moduleKey, title, status, score) => {
    setActiveModule(moduleKey);
    setActiveTitle(title);
    setSelectedStatus(status);
    setSelectedScore(score);
    setShowModal(true);
  };

  useEffect(() => {
    if (!initialOpenKey || trainings.length === 0) return;
    const training = trainings.find((item) => item.tm_key === initialOpenKey);
    if (!training) return;

    handleOpenQuiz(
      training.tm_key,
      training.tm_title,
      getStatus(training.status, training.score, training.passingScore),
      training.score,
    );
    navigate(location.pathname, { replace: true, state: {} });
  }, [initialOpenKey, trainings, navigate, location.pathname]);

  const handleReturn = () => {
    navigate(isAdmin && paramId ? `/userhome/${paramId}` : "/userhome");
  };

  const activeRows = activeTab === "assigned" ? trainings : history;
  const pageStart = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRows = activeRows.slice(
    pageStart,
    pageStart + ITEMS_PER_PAGE,
  );
  const answerDetails =
    selectedAttempt?.tah_result_detail?.length > 0
      ? selectedAttempt.tah_result_detail
      : selectedAttempt?.tah_answers || [];
  const showHistoryCorrectAnswers =
    Boolean(selectedAttempt) &&
    getStatus(
      selectedAttempt.tah_status,
      selectedAttempt.tah_score,
      selectedAttempt.tah_passing_score,
    ) === "verified";

  return (
    <>
      <div
        className={`d-flex justify-content-between align-items-center ${
          isAdmin ? "pt-3" : "training-user-page-header"
        }`}
      >
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          Training Panel
        </span>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleReturn}
        >
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Training Panel</h2>
          </div>
          <CompanyAddress />
        </div>

        <ul className="nav nav-tabs training-panel-tabs mb-3">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === "assigned" ? "active" : ""}`}
              onClick={() => setActiveTab("assigned")}
            >
              Assigned
            </button>
          </li>
          {isAdmin && (
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                History
              </button>
            </li>
          )}
        </ul>

        <div className="training-status-legend mb-3">
          <StatusIndicator status="not-started" showLabel />
          <StatusIndicator status="partially-complete" showLabel />
          <StatusIndicator status="redo" showLabel />
          <StatusIndicator status="expired" showLabel />
          <StatusIndicator status="verified" showLabel />
        </div>

        <div className="table-responsive">
          {activeTab === "assigned" ? (
            <table
              key="assigned-training-table"
              className="table table-bordered align-middle"
            >
              <thead className="table-light">
                <tr>
                  <th className="text-center">Status</th>
                  <th>Module Name</th>
                  <th className="text-center">Score</th>
                  <th className="text-center">Passing Score</th>
                  <th className="text-center">Start Date</th>
                  <th className="text-center">Finish Date</th>
                  <th className="text-center">Expiry Date</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((training) => {
                  const status = getStatus(
                    training.status,
                    training.score,
                    training.passingScore,
                  );
                  return (
                    <tr key={training.tm_id}>
                      <td className="text-center">
                        <StatusIndicator status={status} />
                      </td>
                      <td>
                        <button
                          className="btn btn-link p-0"
                          onClick={() =>
                            handleOpenQuiz(
                              training.tm_key,
                              training.tm_title,
                              status,
                              training.score,
                            )
                          }
                        >
                          {training.tm_title}
                        </button>
                      </td>
                      <td className="text-center">{training.score ?? "-"}</td>
                      <td className="text-center">{training.passingScore}</td>
                      <td className="text-center">{training.startDate || "-"}</td>
                      <td className="text-center">{training.finishDate || "-"}</td>
                      <td className="text-center">{training.expiryDate || "-"}</td>
                    </tr>
                  );
                })}
                {paginatedRows.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center text-muted">
                      No assigned training modules.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <table
              key="history-training-table"
              className="table table-bordered align-middle training-history-table"
            >
              <thead className="table-light">
                <tr>
                  <th>Status</th>
                  <th>Module Name</th>
                  <th>Score</th>
                  <th>Passing Score</th>
                  <th>Attempted Date</th>
                  <th>Answers</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((attempt) => (
                  <tr key={attempt.tah_id}>
                    <td>
                      <StatusIndicator
                        status={getStatus(
                          attempt.tah_status,
                          attempt.tah_score,
                          attempt.tah_passing_score,
                        )}
                      />
                    </td>
                    <td>{attempt.tm_title}</td>
                    <td>{attempt.tah_score}</td>
                    <td>{attempt.tah_passing_score}</td>
                    <td>{attempt.attemptedDate || "-"}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setSelectedAttempt(attempt)}
                      >
                        View Answers
                      </button>
                    </td>
                  </tr>
                ))}
                {paginatedRows.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted">
                      No training attempts recorded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          <Pagination
            currentPage={currentPage}
            totalItems={activeRows.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        </div>

        {showModal && (
          <TrainingTaskModal
            show
            onHide={() => {
              setShowModal(false);
              fetchTrainingData();
            }}
            moduleKey={activeModule}
            title={activeTitle}
            userId={userId}
            tpStatus={selectedStatus}
            tpScore={selectedScore}
            onSubmitted={() => {
              fetchTrainingData();
              setShowModal(false);
            }}
          />
        )}

        <Modal
          show={Boolean(selectedAttempt)}
          onHide={() => setSelectedAttempt(null)}
          size="lg"
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedAttempt?.tm_title} - Attempt Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="training-attempt-summary mb-3">
              <strong>Score:</strong> {selectedAttempt?.tah_score}%{" "}
              <span className="mx-2">|</span>
              <strong>Passing Score:</strong>{" "}
              {selectedAttempt?.tah_passing_score}%{" "}
              <span className="mx-2">|</span>
              <strong>Attempted:</strong> {selectedAttempt?.attemptedDate}
            </div>

            {answerDetails.map((answer, index) => {
              const selected = (answer.selected || []).map(String);
              const correct = (answer.correct || []).map(String);
              const options = Array.isArray(answer.options)
                ? answer.options
                : [];

              return (
                <div
                  className="training-history-answer"
                  key={`${answer.questionId || index}-${index}`}
                >
                  <div className="fw-semibold mb-2">
                    {index + 1}.{" "}
                    {answer.question || `Question ${answer.questionId}`}
                  </div>

                  {options.length > 0 ? (
                    <ul className="tq-options mb-0">
                      {options.map((option, optionIndex) => {
                        const letter = String.fromCharCode(65 + optionIndex);
                        const isSelected = selected.includes(letter);
                        const isCorrect = correct.includes(letter);
                        const showThisCorrectAnswer =
                          isCorrect &&
                          (showHistoryCorrectAnswers || isSelected);
                        const optionClass =
                          showThisCorrectAnswer
                            ? "tq-correct"
                            : isSelected && !isCorrect
                              ? "tq-wrong"
                              : "";
                        return (
                          <li
                            className={`tq-option ${optionClass}`}
                            key={`${answer.questionId}-${letter}`}
                          >
                            <span className="me-2">{letter}.</span>
                            <span>
                              {option}
                              {showThisCorrectAnswer && " ✓"}
                              {isSelected && !isCorrect && " ✕"}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <>
                      <div>
                        <strong>Answer:</strong>{" "}
                        {answerText(selected, options)}
                      </div>
                      {showHistoryCorrectAnswers && correct.length > 0 && (
                        <div className="text-muted">
                          <strong>Correct answer:</strong>{" "}
                          {answerText(correct, options)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}

            {answerDetails.length === 0 && (
              <p className="text-muted mb-0">
                No answer details were stored for this attempt.
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setSelectedAttempt(null)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}

export default TrainingPanel;
